import { useEffect, useState } from 'react';

import { Button } from '@/shared/ui/button';
import { SelectField } from '@/shared/ui/select';
import { LoadingStatus } from '@/shared/ui/spinner';

import type { MioLearnerProfile, MioMemory } from '@mio/shared';

export function MioMemoryPanel({
  memory,
  profile,
  voices,
  onSaveProfile,
  onSaveMemory,
  onRemoveMemory,
  onResetMemory,
  onLoadVoices,
  onClose,
}: {
  memory: MioMemory[];
  profile: MioLearnerProfile;
  voices: { id: string; name: string; category: string }[];
  onSaveProfile: (profile: Partial<MioLearnerProfile>) => Promise<void>;
  onSaveMemory: (id: string, text: string) => Promise<void>;
  onRemoveMemory: (id: string) => Promise<void>;
  onResetMemory: () => Promise<void>;
  onLoadVoices: () => Promise<void>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(profile);
  const [editing, setEditing] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState('Saving changes…');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [resetting, setResetting] = useState(false);
  useEffect(() => {
    setDraft(profile);
  }, [profile]);
  const run = async (operation: () => Promise<void>, label = 'Saving changes…') => {
    setBusyLabel(label);
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await operation();
      setEditing(null);
      setSaved(label !== 'Loading voices…');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save changes.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section
      className="min-h-0 flex-1 overflow-y-auto px-5 pb-5"
      aria-label="Mio memory and preferences"
    >
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-fg text-sm font-medium">Memory & preferences</h3>
        <button type="button" onClick={onClose} className="text-fg-muted text-xs underline">
          Back to chat
        </button>
      </div>
      <p className="text-fg-muted mb-4 text-xs leading-5">
        Help Mio choose useful examples and an appropriate pace. You can edit or remove what it
        remembers.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void run(() => onSaveProfile(draft));
        }}
        className="space-y-3"
      >
        {(
          [
            { key: 'background', label: 'Your background', max: 2000 },
            { key: 'goals', label: 'What you want to learn', max: 2000 },
            { key: 'exampleDomain', label: 'Examples that are familiar to you', max: 1000 },
          ] as const
        ).map((field) => (
          <label key={field.key} className="text-fg-muted block text-xs">
            {field.label}
            <textarea
              value={draft[field.key]}
              maxLength={field.max}
              rows={2}
              onChange={(event) =>
                setDraft((current) => ({ ...current, [field.key]: event.target.value }))
              }
              className="border-line bg-canvas text-fg focus:border-line-strong mt-1 w-full resize-y rounded-md border p-2 text-xs leading-5 outline-none"
            />
          </label>
        ))}
        <div className="grid grid-cols-2 gap-3">
          <label className="text-fg-muted text-xs">
            Detail
            <SelectField
              aria-label="Detail"
              value={draft.verbosity}
              onValueChange={(value) =>
                setDraft((current) => ({
                  ...current,
                  verbosity: value as MioLearnerProfile['verbosity'],
                }))
              }
              size="sm"
              className="mt-1 w-full"
              options={[
                { value: 'concise', label: 'Concise' },
                { value: 'balanced', label: 'Balanced' },
                { value: 'detailed', label: 'Detailed' },
              ]}
            />
          </label>
          <label className="text-fg-muted text-xs">
            Pace
            <SelectField
              aria-label="Pace"
              value={draft.pace}
              onValueChange={(value) =>
                setDraft((current) => ({
                  ...current,
                  pace: value as MioLearnerProfile['pace'],
                }))
              }
              size="sm"
              className="mt-1 w-full"
              options={[
                { value: 'slow', label: 'Slow' },
                { value: 'natural', label: 'Natural' },
                { value: 'fast', label: 'Fast' },
              ]}
            />
          </label>
        </div>
        <label className="text-fg-muted flex gap-2 text-xs">
          <input
            type="checkbox"
            checked={draft.quiet}
            onChange={(event) =>
              setDraft((current) => ({ ...current, quiet: event.target.checked }))
            }
          />
          Quiet mode · show responses without narration
        </label>
        <div className="text-fg-muted text-xs">
          <label htmlFor="mio-voice-choice">Voice</label>
          {voices.length ? (
            <SelectField
              id="mio-voice-choice"
              aria-label="Voice"
              value={draft.voiceId ?? ''}
              onValueChange={(value) =>
                setDraft((current) => ({ ...current, voiceId: value || null }))
              }
              size="sm"
              className="mt-1 w-full"
              options={[
                { value: '', label: 'Workspace default' },
                ...voices.map((voice) => ({ value: voice.id, label: voice.name })),
              ]}
            />
          ) : (
            <button
              type="button"
              onClick={() => void run(onLoadVoices, 'Loading voices…')}
              disabled={busy}
              className="border-line mt-1 block w-full rounded border p-2 text-left"
            >
              Choose an available voice
            </button>
          )}
        </div>
        <Button type="submit" size="sm" disabled={busy}>
          Save preferences
        </Button>
      </form>
      <div className="border-line mt-6 border-t pt-4">
        <h4 className="text-fg mb-3 text-xs font-medium">What Mio remembers</h4>
        {!memory.length ? (
          <p className="text-fg-subtle text-xs leading-5">No saved memories yet.</p>
        ) : (
          <ul className="space-y-3">
            {memory.map((item) => (
              <li key={item.id} className="border-line rounded-md border p-3">
                <span className="text-fg-subtle text-xs uppercase tracking-wide">
                  {item.kind}
                  {item.learnerEdited ? ' · edited by you' : ''}
                </span>
                {editing === item.id ? (
                  <>
                    <label className="sr-only" htmlFor={`memory-${item.id}`}>
                      Edit memory
                    </label>
                    <textarea
                      id={`memory-${item.id}`}
                      value={text}
                      onChange={(event) => setText(event.target.value)}
                      maxLength={4000}
                      rows={3}
                      className="border-line bg-canvas text-fg mt-2 w-full rounded border p-2 text-xs"
                    />
                    <div className="mt-2 flex gap-3 text-xs">
                      <button
                        type="button"
                        disabled={busy || !text.trim()}
                        onClick={() => void run(() => onSaveMemory(item.id, text))}
                        className="text-fg underline"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing(null)}
                        className="text-fg-muted"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-fg mt-1 whitespace-pre-wrap text-xs leading-5">
                      {item.text}
                    </p>
                    <div className="text-fg-muted mt-2 flex gap-3 text-xs">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(item.id);
                          setText(item.text);
                        }}
                        className="hover:text-fg"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void run(() => onRemoveMemory(item.id))}
                        className="hover:text-danger"
                      >
                        Remove
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {memory.length ? (
        <div className="mt-4 text-xs">
          {resetting ? (
            <>
              <p className="text-fg-muted mb-2">
                Remove all saved memories? Your conversation and preferences remain.
              </p>
              <button
                type="button"
                disabled={busy}
                className="text-danger underline"
                onClick={() =>
                  void run(async () => {
                    await onResetMemory();
                    setResetting(false);
                  })
                }
              >
                Remove all memories
              </button>
              <button
                type="button"
                className="text-fg-muted ml-3"
                onClick={() => setResetting(false)}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              className="text-fg-muted underline"
              onClick={() => setResetting(true)}
            >
              Reset saved memory
            </button>
          )}
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="text-danger mt-3 text-xs">
          {error}
        </p>
      ) : null}
      {busy ? <LoadingStatus label={busyLabel} className="mt-3 text-xs" /> : null}
      {saved ? (
        <p role="status" className="text-fg-muted mt-3 text-xs">
          Saved.
        </p>
      ) : null}
    </section>
  );
}
